import { useState, useMemo, useEffect, useRef, useId } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import { searchProjectFilenameMatches } from "@/stores/documents/repository";
import { folderService, getDescendantFolderIds } from "@/domains/documents/services/folderService";
import {
  folderLabel,
  tagBriefFolder,
  tagIssueAttachmentsFolder,
} from "@/domains/documents/utils/briefFolder";
import { useProjectBriefFolder } from "@/domains/brief/hooks/useProjectBriefFolder";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import type { TreeFolder } from "@/domains/documents/types";
import { FolderIcon as ProjectFolderIcon } from "@/domains/documents/components/atoms/FolderIcon";
import { ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useVersionExpansion } from "@/domains/documents/hooks/useVersionExpansion";
import type { DocumentRowMapped } from "@/stores/documents/repository/types";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type {
  DocumentPickerProps,
  PickerDocument,
  PickerSourceDocument,
  RawDocument,
} from "./documentPickerTypes";
import { DocumentPickerList } from "./DocumentPickerList";
import { PickerFolderTree } from "./PickerFolderTree";
import { findFolderInTree } from "./pickerTreeUtils";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { ProjectSelectContent } from "@/domains/projects/status/ProjectSelectContent";
import { useProjectStatusFilter } from "@/domains/projects/status/useProjectStatusFilter";

export type {
  DocumentPickerProps,
  PickerDocument,
  PickerSourceDocument,
} from "./documentPickerTypes";

/** The identity of a logical file: its lineage, or itself when it has none. */
function lineageKey(doc: { id: number; version_group?: string | null }): string {
  return doc.version_group ?? `doc:${doc.id}`;
}

/**
 * The ids that get a top-level row of their own — one per lineage.
 *
 * A row with no `version_group` is its own lineage whatever its latest-version
 * flag says — nesting it would nest it under nothing, so it would vanish from
 * the modal while the callers' `groupCandidatesIntoChains` still counts it as a
 * chain, making "Browse all {n}" promise files the modal never shows.
 *
 * The same holds for a lineage NO row of which is flagged latest: per-row, every
 * one of them nests and the file disappears. So the newest revision heads it,
 * which is exactly how `groupCandidatesIntoChains` elects its head — the count
 * and the list are then answering with the same rule.
 */
function sourceHeadIds(docs: PickerSourceDocument[]): Set<number> {
  const lineages = new Map<string, PickerSourceDocument[]>();
  for (const doc of docs) {
    const key = lineageKey(doc);
    const bucket = lineages.get(key);
    if (bucket) bucket.push(doc);
    else lineages.set(key, [doc]);
  }
  const heads = new Set<number>();
  for (const members of lineages.values()) {
    const newestFirst = [...members].sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
    heads.add((newestFirst.find((d) => d.is_latest_version) ?? newestFirst[0]).id);
  }
  return heads;
}

/** `DocumentRowMapped` (what a version-chain fetch returns) → a picker row. */
function mapVersionRow(row: DocumentRowMapped): RawDocument {
  return {
    id: Number(row.id),
    filename: row.filename,
    content_type: row.content_type ?? null,
    folder_id: row.folder_id ?? null,
    version: row.version ?? null,
    version_group: row.version_group ?? null,
    is_latest_version: row.is_latest_version ?? null,
  };
}

/**
 * Rows one subtree search may return. The search is capped server-side either
 * way; passing the cap explicitly is what lets the list say so when it is hit,
 * instead of presenting a truncated result as the whole answer.
 */
const SEARCH_MATCH_LIMIT = 200;

/** Subtree search result: the rows, plus whether the search hit the cap. */
type SubfolderSearchResult = { rows: RawDocument[]; truncated: boolean };
// Stable reference: a fresh object literal as the query default would be a new
// dependency on every render for the memos below.
const EMPTY_SEARCH_RESULT: SubfolderSearchResult = { rows: [], truncated: false };

export const DocumentPicker = observer(function DocumentPicker({
  open,
  onOpenChange,
  buildingId,
  alreadyAttachedIds,
  mode,
  onSelect,
  title,
  description,
  submitLabel,
  projectId,
  sourceDocuments,
  initialSelectedIds,
  maxSelection,
  showVersionHistory = false,
  defaultProjectId,
  unselectableVersionGroups,
  unselectableReason,
  attachedLabel,
}: DocumentPickerProps) {
  const { t } = useLanguage();
  const copy = t.documents.picker;
  const documentStore = useDocumentStore();
  const queryClient = useQueryClient();
  // The document query keys carry this instance's id so the reset-on-open
  // removal below can drop THIS picker's entries only. The keys are otherwise
  // identical for every picker on the same building — the sidebar chat picker
  // is scoped to the same building as an issue picker opened over it — so a
  // prefix removal would empty a sibling's cache and make it refetch from
  // nothing. Nothing is shared by keeping them apart: each open refetches
  // anyway (`staleTime: 0`).
  const pickerId = useId();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  /** Expanded version chains in source mode, where nothing needs fetching. */
  const [locallyExpanded, setLocallyExpanded] = useState<Set<string>>(() => new Set());
  const seedRef = useRef({ alreadyAttachedIds, initialSelectedIds, defaultProjectId });
  seedRef.current = { alreadyAttachedIds, initialSelectedIds, defaultProjectId };
  const sourceRef = useRef<PickerSourceDocument[] | undefined>(sourceDocuments);
  sourceRef.current = sourceDocuments;
  // Every document ticked during this opening, by id. `selectedDocIds` outlives
  // the lists a row was ticked from — both document queries are keyed by folder
  // AND search term, so refining the term or walking to another folder leaves
  // the id selected with no row behind it. Resolving the submit set from the
  // visible rows then drops those documents from `onSelect` while the footer
  // still counts them; resolving it from here can't.
  const selectedDocsRef = useRef(new Map<string, RawDocument>());

  // Debounce search query (300ms). Clearing is applied at once: every
  // "leave this list" action (open, folder click, breadcrumb, project
  // change) clears the input, and a lagging debounced term would keep the
  // new list filtered by the old one — and fire the subtree search query
  // for it — for 300ms.
  useEffect(() => {
    if (!searchQuery) {
      setDebouncedSearchQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { projects, isError: projectsFailed } = useProjects(buildingId);
  const projectStatusFilter = useProjectStatusFilter(projects, selectedProjectId);

  // Auto-select project when dialog opens.
  // WHA-3393: when the caller passes a `projectId` prop the picker is
  // locked to that project (the dropdown is hidden below). Otherwise
  // fall back to: if only 1 project exists, select it; if multiple, pick
  // the default-marked one.
  useEffect(() => {
    if (!open) return;
    if (projectId) {
      // Always reset to the locked project — even if the user previously
      // picked something else while reusing the same picker instance.
      setSelectedProjectId(projectId);
      return;
    }
    if (projects.length === 0 || selectedProjectId !== null) return;
    if (projects.length === 1) {
      setSelectedProjectId(projects[0].id);
    } else {
      const defaultProject = projects.find((p) => p.is_default);
      if (defaultProject) {
        setSelectedProjectId(defaultProject.id);
      }
    }
  }, [open, projectId, projects, selectedProjectId]);

  // Reset from the controlled `open` prop. Radix does not emit
  // `onOpenChange(true)` when a parent opens a controlled dialog, so doing
  // this in the callback leaves the project cache untouched. A superseded
  // document row can then remain in the picker even though the repository
  // query correctly requests only the latest version.
  useEffect(() => {
    // Nothing to reset on the way out — and the project stays selected on
    // purpose. Radix keeps the content mounted for the close animation
    // (`data-[state=closed]:animate-out`, 200ms), so clearing it here empties
    // the project select, the folder pane and the breadcrumb of every closing
    // dialog, including the successful attach path. The reopen guarantee does
    // not need it: this effect and the invalidation below both re-run on open
    // — the latter re-invalidates even for an unchanged project, because its
    // ref is reset on close — and both run before the queries subscribe.
    if (!open) return;

    // These queries are keyed by folder + project, so reopening on the same
    // folder hands back the previous array synchronously and only then
    // refetches — rendering (and offering) a superseded version until it
    // resolves. The store's own cache is dropped by the effect below.
    queryClient.removeQueries({ queryKey: ["doc-picker-docs", pickerId] });
    queryClient.removeQueries({ queryKey: ["doc-picker-subfolder-docs", pickerId] });
    const seed = seedRef.current;
    setSelectedDocIds(new Set([...seed.alreadyAttachedIds, ...(seed.initialSelectedIds ?? [])]));
    // Rows for the seeded ids, so a pre-ticked file survives Apply without the
    // user re-ticking it. Only source mode can do this — it is also the only
    // mode `initialSelectedIds` is used from (see the prop's comment).
    const seededDocs = (sourceRef.current ?? []).filter((d) =>
      (seed.initialSelectedIds ?? []).includes(String(d.id))
    );
    selectedDocsRef.current = new Map(seededDocs.map((d) => [String(d.id), d as RawDocument]));
    setSelectedFolderId(null);
    setExpandedFolders(new Set());
    // Open the chain behind every seeded pick. A seeded id can name a
    // SUPERSEDED revision, which has a row only inside an expanded chain — and
    // a caller passing `maxSelection` disables every other row once the seeds
    // fill the cap, so a collapsed chain leaves the whole modal inert: nothing
    // ticked to untick, nothing else tickable.
    setLocallyExpanded(new Set(seededDocs.map(lineageKey)));
    setSearchQuery("");
    setDebouncedSearchQuery("");
    // A default is re-applied on every open, like the lock, so a reused picker
    // opens on the caller's project rather than wherever it was last left. It
    // runs after the auto-select effect above in the same pass, so it wins.
    if (projectId) setSelectedProjectId(projectId);
    else if (seed.defaultProjectId) setSelectedProjectId(seed.defaultProjectId);
  }, [open, pickerId, projectId, queryClient]);

  // The folder the user is looking at is read fresh: its cached slot may hold
  // a version a later upload has superseded, which is exactly what the picker
  // must never offer. This deliberately does NOT invalidate the project first.
  // `invalidateProjectCache` DELETES that project's slots without bumping
  // `projectCacheVersion` or reloading anything, so it empties the cache under
  // whatever documents surface is open behind the dialog: the next version
  // bump from anywhere (a rename, an upload) recomputes that view from a slot
  // that is gone and renders an empty folder, with nothing in flight to refill
  // it. A fresh peek writes the slot back instead of removing it, which leaves
  // that surface strictly better off. The cost is one request per folder the
  // picker opens, where the cache would previously have answered a revisit
  // within the same opening.
  //
  // The subtree search below is fresh for the same reason, and gets there
  // without a fan-out: `searchProjectFilenameMatches` is one server-side query
  // over every descendant folder (the same one the Folders tab searches with),
  // so a filtered list can't offer a superseded row a cached slot still holds,
  // and a project with fifty folders costs one request per search run rather
  // than one per folder.

  // Fetch folder tree via folderService
  const folderTreeEnabled = open && !!buildingId;
  const {
    data: folderTree = [],
    isError: folderTreeFailed,
    isPending: folderTreePending,
  } = useQuery({
    queryKey: ["doc-picker-folders", buildingId],
    queryFn: async () => {
      const folders = await folderService.getFolders(buildingId);
      return folderService.buildFolderTree(folders);
    },
    enabled: folderTreeEnabled,
  });
  // Same reason the document pane has one: `projectFolderTree` is empty for
  // the whole round-trip, so an ungated pane claims "No folders yet" about a
  // project whose folders simply haven't arrived. The key is not scoped to
  // this picker and is never removed, so a reopen resolves from cache and
  // this is false — the skeleton is the first load only.
  const folderTreeLoading = folderTreeEnabled && folderTreePending;

  // In source mode every revision is already in memory. Split it once into the
  // heads shown as rows and the superseded revisions nested under them.
  const headIds = useMemo(() => sourceHeadIds(sourceDocuments ?? []), [sourceDocuments]);
  const sourceHeads = useMemo(
    () => (sourceDocuments ?? []).filter((d) => headIds.has(d.id)),
    [sourceDocuments, headIds]
  );

  // Filter folder tree by selected project
  const briefFolder = useProjectBriefFolder(selectedProjectId ? Number(selectedProjectId) : null);
  const projectFolderTree = useMemo(() => {
    if (!selectedProjectId) return [];
    const pid = Number(selectedProjectId);
    // The Brief folder is shown as the brief, as on /documents — tagged here,
    // never in the shared cache entry.
    const scoped = tagIssueAttachmentsFolder(
      tagBriefFolder(
        folderTree.filter((f) => f.project_id === pid),
        briefFolder.folderId,
        t.brief.title
      ),
      t.documents.folders.issueAttachments
    );
    if (!sourceDocuments) return scoped;

    // Source mode is a filtered view (the viewers pass only DWG or only IFC),
    // so most folders hold nothing selectable. Keep a folder only when it — or
    // a descendant — has a file, so the tree stops being a field of dead ends
    // while every surviving file stays reachable by its real path.
    //
    // Counted over the HEADS, which is what the document pane lists: a folder
    // holding only superseded revisions (the current one having been uploaded
    // elsewhere) would otherwise survive the prune and then open empty.
    const occupied = new Set(
      sourceHeads.map((d) => (d.folder_id == null ? "" : String(d.folder_id)))
    );
    const prune = (folders: TreeFolder[]): TreeFolder[] =>
      folders
        .map((f) => ({ ...f, children: prune(f.children) }))
        .filter((f) => f.children.length > 0 || occupied.has(f.id));
    return prune(scoped);
  }, [
    folderTree,
    selectedProjectId,
    sourceDocuments,
    sourceHeads,
    briefFolder.folderId,
    t.brief.title,
    t.documents.folders.issueAttachments,
  ]);

  const sourceOlderByLineage = useMemo(() => {
    const map = new Map<string, RawDocument[]>();
    for (const d of sourceDocuments ?? []) {
      if (headIds.has(d.id)) continue;
      const key = lineageKey(d);
      const bucket = map.get(key);
      if (bucket) bucket.push(d);
      else map.set(key, [d]);
    }
    // Newest revision first, matching the fetched path's ordering.
    for (const list of map.values()) list.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
    return map;
  }, [sourceDocuments, headIds]);

  // Child folders of the currently selected folder
  const childFolders = useMemo(() => {
    if (!selectedProjectId) return [];
    if (!selectedFolderId) return projectFolderTree;
    const folder = findFolderInTree(projectFolderTree, selectedFolderId);
    return folder?.children ?? [];
  }, [selectedFolderId, projectFolderTree, selectedProjectId]);

  // Documents for the selected folder
  const documentsEnabled = open && !!buildingId && !!selectedProjectId && !sourceDocuments;
  const {
    data: fetchedDocuments = [],
    isError: documentsFailed,
    isPending: documentsPending,
    refetch: refetchDocuments,
  } = useQuery<RawDocument[]>({
    queryKey: ["doc-picker-docs", pickerId, buildingId, selectedFolderId, selectedProjectId],
    queryFn: async () => {
      // `documentsEnabled` already requires a project; narrowing it here is
      // what lets the call drop its non-null assertion.
      if (!selectedProjectId) return [];
      const docs = await documentStore.peekFolderDocuments(selectedProjectId, selectedFolderId, {
        fresh: true,
      });
      return (docs ?? []).map((d: Record<string, unknown>) => ({
        id: d.id as number,
        filename: d.filename as string,
        content_type: (d.content_type as string) ?? null,
        folder_id: (d.folder_id as number) ?? null,
        version: (d.version as number) ?? null,
        version_group: (d.version_group as string) ?? null,
        // The folder peek is latest-only (`applyVisibleDocumentFilters`).
        is_latest_version: true,
      }));
    },
    enabled: documentsEnabled,
    staleTime: 0,
  });

  // Source mode answers the folder pane from memory: the heads filed under the
  // open folder, with project root holding the files that have no folder.
  const documents = useMemo(() => {
    if (!sourceDocuments) return fetchedDocuments;
    return sourceHeads.filter((d) =>
      selectedFolderId === null ? d.folder_id == null : String(d.folder_id) === selectedFolderId
    );
  }, [sourceDocuments, sourceHeads, fetchedDocuments, selectedFolderId]);

  // Folders the subtree search covers: the open folder AND its descendants, or
  // the whole project — `null`, no folder filter — at project root. Enumerating
  // every folder there instead puts the whole list in the request URL, growing
  // it with the project (the ids are chunked server-side, but a project-wide
  // list is still pointless traffic for a filter that is already unconstrained).
  // Same call the Folders tab makes for the same scope.
  //
  // The open folder belongs in that list even though `documents` holds its
  // rows: that peek is capped (`fetchProjectDocuments`, limit 100), so in a
  // folder holding more than the cap a matching file is simply absent from the
  // list — which, under the "showing the first N matches" banner, reads as an
  // authoritative "not here".
  const searchFolderIds = useMemo<number[] | null>(() => {
    if (!selectedProjectId) return [];
    if (!selectedFolderId) return null;
    const currentFolder = findFolderInTree(projectFolderTree, selectedFolderId);
    if (!currentFolder) return [];
    return [currentFolder.id, ...getDescendantFolderIds(currentFolder)].map(Number);
  }, [selectedFolderId, projectFolderTree, selectedProjectId]);

  const subfolderDocumentsEnabled =
    open &&
    !!buildingId &&
    !!selectedProjectId &&
    !sourceDocuments &&
    (searchFolderIds === null || searchFolderIds.length > 0) &&
    !!debouncedSearchQuery.trim();
  const {
    data: subfolderSearch = EMPTY_SEARCH_RESULT,
    isError: subfolderDocumentsFailed,
    isPending: subfolderDocumentsPending,
    refetch: refetchSubfolderDocuments,
  } = useQuery<SubfolderSearchResult>({
    queryKey: [
      "doc-picker-subfolder-docs",
      pickerId,
      buildingId,
      selectedProjectId,
      selectedFolderId,
      searchFolderIds,
      debouncedSearchQuery,
    ],
    queryFn: async () => {
      if (!selectedProjectId || searchFolderIds?.length === 0) return EMPTY_SEARCH_RESULT;
      // `throwOnError`: the default is to log and hand back no rows, which
      // here reads as "nothing else matches" — the search banner below can
      // only offer its retry if the failure reaches the query.
      const { documents: rows, truncated } = await searchProjectFilenameMatches({
        buildingId,
        projectId: Number(selectedProjectId),
        folderIds: searchFolderIds,
        searchText: debouncedSearchQuery,
        limit: SEARCH_MATCH_LIMIT,
        throwOnError: true,
      });
      return {
        rows: rows.map((d) => ({
          id: d.id,
          filename: d.filename,
          content_type: d.content_type,
          folder_id: d.folder_id,
          version: d.version ?? null,
          version_group: d.version_group ?? null,
          // Latest-only, like the folder peek.
          is_latest_version: true,
        })),
        truncated,
      };
    },
    enabled: subfolderDocumentsEnabled,
    staleTime: 0,
  });

  // Folder id → breadcrumb path
  const folderBreadcrumbMap = useMemo(() => {
    const map = new Map<string, string>();
    function buildPath(folders: TreeFolder[], parentPath: string[]) {
      for (const folder of folders) {
        const currentPath = [...parentPath, folderLabel(folder)];
        map.set(folder.id, currentPath.join(" > "));
        if (folder.children.length > 0) buildPath(folder.children, currentPath);
      }
    }
    buildPath(projectFolderTree, []);
    return map;
  }, [projectFolderTree]);

  // Reported by the search itself, over the ids it matched — NOT derived from
  // the rows below. Those are filtered again on the way back (deleted, enclosed
  // in a signed container), so a capped search that loses one row to that
  // filter would count as complete and drop the banner, leaving a cut-off list
  // reading as "that file isn't in this project" — the one answer we know is
  // wrong. Counting rows also raises the banner over exactly-`limit` matches
  // that were all returned.
  const { rows: fetchedSubfolderDocuments, truncated: fetchedSearchTruncated } = subfolderSearch;

  // Searching in source mode spans the whole project and can't be truncated:
  // the rows are already here, so a search reaches files the fetched path
  // needs a server round-trip — and its 200-match cap — to find.
  const subfolderDocuments = sourceDocuments ? sourceHeads : fetchedSubfolderDocuments;
  const searchTruncated = sourceDocuments ? false : fetchedSearchTruncated;

  const filteredDocuments = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return documents;
    const q = debouncedSearchQuery.toLowerCase();
    const combined = [...documents, ...subfolderDocuments];
    const seen = new Set<number>();
    const deduped: RawDocument[] = [];
    for (const doc of combined) {
      if (!seen.has(doc.id)) {
        seen.add(doc.id);
        deduped.push(doc);
      }
    }
    return deduped.filter((d) => d.filename.toLowerCase().includes(q));
  }, [documents, subfolderDocuments, debouncedSearchQuery]);

  const filteredChildFolders = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return childFolders;
    const q = debouncedSearchQuery.toLowerCase();
    const matchingFolders: TreeFolder[] = [];
    function collect(folders: TreeFolder[]) {
      for (const folder of folders) {
        if (
          folder.name.toLowerCase().includes(q) ||
          folderLabel(folder).toLowerCase().includes(q)
        ) {
          matchingFolders.push(folder);
        }
        collect(folder.children);
      }
    }
    const searchRoot = selectedFolderId
      ? (findFolderInTree(projectFolderTree, selectedFolderId)?.children ?? [])
      : projectFolderTree;
    collect(searchRoot);
    return matchingFolders;
  }, [childFolders, debouncedSearchQuery, selectedFolderId, projectFolderTree]);

  // Version chains. In source mode every revision is already in memory; on the
  // fetched path the list is latest-only, so a file's history is fetched the
  // first time its chevron is opened.
  const versionExpansion = useVersionExpansion(mapVersionRow);

  /**
   * The revisions to nest under a row, and whether to offer the chevron at all.
   *
   * In source mode both are known outright. On the fetched path a head at v2 or
   * higher must have had predecessors, so the chevron is offered on that alone —
   * but they may since have been hard-deleted, which is why an empty result is
   * reported rather than rendered as nothing. That path is opt-in
   * (`showVersionHistory`): every other caller of it takes what `onSelect`
   * returns and writes it, and never asked for a non-head document.
   */
  const olderVersionsFor = (doc: RawDocument): RawDocument[] | undefined =>
    sourceDocuments
      ? (sourceOlderByLineage.get(lineageKey(doc)) ?? [])
      : versionExpansion.getVersions(doc.version_group);

  const canExpandVersions = (doc: RawDocument): boolean =>
    sourceDocuments
      ? (sourceOlderByLineage.get(lineageKey(doc))?.length ?? 0) > 0
      : showVersionHistory && !!doc.version_group && (doc.version ?? 1) > 1;

  const versionsExpanded = (doc: RawDocument): boolean =>
    sourceDocuments
      ? locallyExpanded.has(lineageKey(doc))
      : versionExpansion.isExpanded(doc.version_group);

  const toggleVersions = (doc: RawDocument) => {
    if (sourceDocuments) {
      // Nothing to fetch — flip the row open or shut.
      setLocallyExpanded((prev) => {
        const next = new Set(prev);
        const key = lineageKey(doc);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      return;
    }
    void versionExpansion.toggle(doc.version_group ?? null, doc.id);
  };

  const versionsLoading = (doc: RawDocument): boolean =>
    !sourceDocuments && versionExpansion.isLoading(doc.version_group);

  const projectName =
    projects.find((p) => p.id === selectedProjectId)?.name ?? copy.projectFallback;

  const breadcrumb = useMemo(() => {
    const root = { id: null as string | null, name: selectedProjectId ? projectName : copy.files };
    if (!selectedFolderId) return [root];
    const path: Array<{ id: string | null; name: string }> = [root];
    function findPath(folders: TreeFolder[], targetId: string): boolean {
      for (const folder of folders) {
        if (folder.id === targetId) {
          path.push({ id: folder.id, name: folderLabel(folder) });
          return true;
        }
        if (folder.children.length > 0) {
          path.push({ id: folder.id, name: folderLabel(folder) });
          if (findPath(folder.children, targetId)) return true;
          path.pop();
        }
      }
      return false;
    }
    findPath(projectFolderTree, selectedFolderId);
    return path;
  }, [selectedFolderId, projectFolderTree, selectedProjectId, projectName, copy.files]);

  const toggleExpand = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const toggleDocSelection = (doc: RawDocument) => {
    const docId = String(doc.id);
    selectedDocsRef.current.set(docId, {
      ...doc,
      project_id: doc.project_id ?? (selectedProjectId !== null ? Number(selectedProjectId) : null),
    });
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (mode === "single") {
        next.clear();
        if (!prev.has(docId)) next.add(docId);
      } else if (next.has(docId)) {
        next.delete(docId);
      } else {
        // Refuse the tick at the cap rather than silently evicting an earlier
        // pick — the untickable rows make the limit visible before it bites.
        // Counted over the ids that RESOLVE to a row, exactly as `newSelections`
        // and therefore `isSelectionBlocked` do: a seeded id with no row would
        // otherwise fill a slot here while the checkbox still rendered enabled,
        // and the tick would be refused with nothing on screen explaining it.
        const attached = new Set(alreadyAttachedIds);
        const picked = [...next].filter(
          (id) => !attached.has(id) && selectedDocsRef.current.has(id)
        ).length;
        if (maxSelection !== undefined && picked >= maxSelection) return prev;
        next.add(docId);
      }
      return next;
    });
  };

  // What Add would hand over, resolved ONCE and used for both the submit and
  // the footer count. Counting the ids while submitting the resolved rows lets
  // the two disagree: the reset seeds `selectedDocIds` from the attached list
  // without capturing rows for those ids, so an id the host drops mid-open
  // (realtime update, parent refetch) stops being "already attached", still
  // counts, and resolves to nothing — the footer would say one document and
  // `onSelect` receive none, with no error anywhere. Recomputed per render
  // rather than memoised: `selectedDocsRef` is a ref, and every tick that can
  // change this also sets `selectedDocIds`, which renders.
  const newSelections = useMemo(() => {
    const alreadySet = new Set(alreadyAttachedIds);
    return [...selectedDocIds]
      .filter((id) => !alreadySet.has(id))
      .map((id) => selectedDocsRef.current.get(id))
      .filter((d): d is RawDocument => d !== undefined)
      .map<PickerDocument>((d) => ({
        id: String(d.id),
        filename: d.filename,
        content_type: d.content_type,
        folder_id: d.folder_id,
        version: d.version,
        version_group: d.version_group,
        is_latest_version: d.is_latest_version ?? null,
        project_id: d.project_id ?? null,
      }));
  }, [selectedDocIds, alreadyAttachedIds]);

  const handleSubmit = () => {
    onSelect(newSelections, selectedProjectId);
  };

  const newSelectionsCount = newSelections.length;

  const unselectableGroups = useMemo(
    () => new Set(unselectableVersionGroups ?? []),
    [unselectableVersionGroups]
  );
  /** Why a row may not be picked at all, or null. Covers a lineage's head and
   *  its nested revisions alike, since they share the version group. */
  const unselectableReasonFor = (doc: RawDocument): string | null =>
    doc.version_group != null && unselectableGroups.has(doc.version_group)
      ? (unselectableReason ?? "")
      : null;

  /** At the cap and not already picked — the row shows but can't be ticked. */
  const isSelectionBlocked = (docId: string): boolean =>
    mode === "multi" &&
    maxSelection !== undefined &&
    newSelectionsCount >= maxSelection &&
    !selectedDocIds.has(docId);

  const navigateToFolder = (folderId: string) => {
    setSelectedFolderId(folderId);
    toggleExpand(folderId);
    setSearchQuery("");
  };

  // Without these an empty list is the only thing a pending or failed fetch
  // produces, which reads as "this folder is empty" — the one message that is
  // certainly wrong when we have no answer yet, or never got one. The reset on
  // open removes the cached array, so every open starts with nothing to show.
  //
  // The two queries are kept apart: the folder's own documents ARE the pane, so
  // their failure replaces it, while the subtree search is an addition to a
  // list that already loaded — failing it must not take the visible documents
  // and subfolders down with it.
  const listLoading =
    (documentsEnabled && documentsPending) ||
    (subfolderDocumentsEnabled && subfolderDocumentsPending);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `pointer-events-auto` and a z above the default 50 so this still works
          when it is opened from inside another modal dialog. A modal Radix
          dialog sets `pointer-events: none` on the body, and this portals to
          the body too — without these it renders and silently ignores every
          click (the template form's "choose file from project" did exactly
          that). Both are no-ops when it is the only dialog on screen. */}
      {/* z-[80] over a z-[75] backdrop, above the z-[70] the viewer file
          pickers' dropdown menus sit at — this dialog is launched from inside
          one of them, so anything lower renders behind the menu that opened
          it. Lowering the menu instead does not work: Radix's popper copies
          the menu's z-index onto its transformed wrapper once on mount, and
          that wrapper — not the menu content — is what competes. */}
      <DialogContent
        className="pointer-events-auto z-[80] flex h-[640px] max-h-[85vh] max-w-3xl flex-col"
        overlayClassName="z-[75]"
      >
        <DialogHeader>
          <DialogTitle>{title ?? copy.title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {/* Project picker — hidden when the caller has locked the project
            (WHA-3393): in that mode the picker is scoped to the issue's
            building_project_id and the user can't choose another one. */}
        {!projectId && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {copy.project}
            </span>
            <Select
              value={selectedProjectId ?? ""}
              onValueChange={(val) => {
                setSelectedProjectId(val);
                setSelectedFolderId(null);
                setSearchQuery("");
              }}
            >
              <SelectTrigger className="h-9 max-w-sm flex-1 text-sm">
                <SelectValue placeholder={copy.selectProject} />
              </SelectTrigger>
              {/* Portals to the body at the default z-50, which would paint
                  under the dialog panel above — so it clears the panel's
                  z-[80] the same way the panel clears the menus' z-[70]. */}
              <ProjectSelectContent statusFilter={projectStatusFilter} className="z-[90]">
                {/* Every project stays mounted — see isShown. */}
                {projectStatusFilter.ordered.map((p) => (
                  <SelectItem
                    key={p.id}
                    value={p.id}
                    disabled={!projectStatusFilter.isShown(p)}
                    className={cn(!projectStatusFilter.isShown(p) && "hidden")}
                  >
                    <div className="flex items-center gap-2">
                      <ProjectFolderIcon isOpen={false} />
                      <ProjectStatusBadge variant="dot" status={p.status} />
                      <span>{p.name}</span>
                      {p.is_default && (
                        <span className="text-[10px] text-muted-foreground">
                          {copy.defaultProject}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </ProjectSelectContent>
            </Select>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          {breadcrumb.map((crumb, i) => {
            // WHA: the root crumb is the project — show the red project
            // icon and emphasize it so the current project is clear,
            // especially when the project picker is locked/hidden.
            const isProjectRoot = i === 0 && !!selectedProjectId;
            return (
              <span key={crumb.id ?? "root"} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="h-3 w-3" />}
                <button
                  className={cn(
                    "flex items-center gap-1 hover:text-foreground hover:underline",
                    isProjectRoot && "font-medium text-foreground"
                  )}
                  onClick={() => {
                    setSelectedFolderId(crumb.id);
                    setSearchQuery("");
                  }}
                >
                  {isProjectRoot && <ProjectFolderIcon isOpen={false} />}
                  {crumb.name}
                </button>
              </span>
            );
          })}
        </div>

        {/* Two-panel layout. `min-h-0` (WHA-3393) lets the flex child shrink
            below its content so the footer stays reachable when the viewport
            is short — e.g. when the browser is zoomed in. */}
        <div className="flex min-h-0 flex-1 gap-3 overflow-hidden rounded-md border">
          {/* Left panel: folder tree */}
          <div className="w-[220px] shrink-0 overflow-y-auto border-r bg-muted/30 py-2">
            {selectedProjectId && folderTreeLoading && (
              <div className="space-y-2 px-3 py-2" aria-label={copy.loadingFolders} role="status">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            )}
            {selectedProjectId && !folderTreeLoading && projectFolderTree.length > 0 && (
              <PickerFolderTree
                folders={projectFolderTree}
                selectedFolderId={selectedFolderId}
                expandedFolderIds={expandedFolders}
                onSelect={(folderId) => {
                  setSelectedFolderId(folderId);
                  setSearchQuery("");
                }}
                onToggle={toggleExpand}
              />
            )}
            {selectedProjectId && folderTreeFailed && (
              <p className="px-3 py-2 text-xs text-destructive">{copy.foldersFailed}</p>
            )}
            {selectedProjectId &&
              !folderTreeLoading &&
              !folderTreeFailed &&
              projectFolderTree.length === 0 && (
                <p className="px-3 py-2 text-xs text-muted-foreground">{copy.noFolders}</p>
              )}
          </div>

          {/* Right panel: document list */}
          <div className="flex flex-1 flex-col overflow-hidden py-2 pr-2">
            <DocumentPickerList
              state={{
                selectedProjectId,
                projectsFailed,
                searchQuery,
                debouncedSearchQuery,
                selectedFolderId,
                subfolderDocumentsFailed,
                documentsFailed,
                searchTruncated,
                listLoading,
                filteredChildFolders,
                filteredDocuments,
                selectedDocIds,
                alreadyAttachedIds,
                folderBreadcrumbMap,
              }}
              versions={{
                canExpand: canExpandVersions,
                isExpanded: versionsExpanded,
                isLoading: versionsLoading,
                older: olderVersionsFor,
                toggle: toggleVersions,
              }}
              isSelectionBlocked={isSelectionBlocked}
              unselectableReasonFor={unselectableReasonFor}
              attachedLabel={attachedLabel ?? copy.attached}
              actions={{
                setSearchQuery,
                retrySubfolderDocuments: () => void refetchSubfolderDocuments(),
                retryDocuments: () => void refetchDocuments(),
                navigateToFolder,
                toggleDocument: toggleDocSelection,
              }}
              searchMatchLimit={SEARCH_MATCH_LIMIT}
            />
          </div>
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {newSelectionsCount === 0
              ? copy.nothingSelected
              : newSelectionsCount === 1
                ? copy.selectedOne
                : copy.selectedMany.replace("{count}", String(newSelectionsCount))}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t.common.cancel}
            </Button>
            <Button onClick={handleSubmit} disabled={newSelectionsCount === 0}>
              {submitLabel ?? copy.submit}
              {newSelectionsCount > 0 ? ` (${newSelectionsCount})` : ""}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
